import React, { useState } from "react";

function UserLogin({ onUsernameSubmit, isDarkMode, toggleDarkMode }) {
  const [username, setUsername] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      setError("Please enter a username");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Test if the username exists by fetching their submissions
      const response = await fetch(`https://codeforces.com/api/user.status?handle=${username.trim()}&count=1`);
      const data = await response.json();

      if (data.status === "OK") {
        onUsernameSubmit(username.trim());
      } else {
        setError(data.comment || "Username not found. Please check the spelling.");
      }
    } catch (err) {
      setError("Failed to verify username. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen font-['Inter'] transition-all duration-500 ${
        isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100" : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
      }`}
    >
      {/* Header */}
      <header className={`backdrop-blur-xl border-b transition-all duration-500 ${isDarkMode ? "bg-slate-900/80 border-slate-700/50" : "bg-white/80 border-slate-200/50"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-2xl ${isDarkMode ? "bg-gradient-to-br from-blue-500 to-purple-600" : "bg-gradient-to-br from-blue-600 to-indigo-700"}`}>
                <i className="fas fa-code text-white text-xl"></i>
              </div>
              <div>
                <h1 className={`text-2xl font-bold bg-gradient-to-r ${isDarkMode ? "from-slate-100 to-slate-300" : "from-slate-900 to-slate-700"} bg-clip-text text-transparent`}>
                  Codeforces Problem Selector
                </h1>
                <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Advanced problem filtering & discovery</p>
              </div>
            </div>
            <button
              onClick={toggleDarkMode}
              className={`p-3 rounded-xl transition-all duration-300 hover:scale-105 ${
                isDarkMode ? "bg-slate-800/50 hover:bg-slate-700/50 text-slate-300" : "bg-slate-100/80 hover:bg-slate-200/80 text-slate-600"
              }`}
              title="Toggle dark mode"
            >
              <i className={`fas ${isDarkMode ? "fa-sun" : "fa-moon"}`}></i>
            </button>
          </div>
        </div>
      </header>

      {/* Login Form */}
      <div className="flex items-center justify-center min-h-[calc(100vh-120px)] px-6">
        <div
          className={`w-full max-w-lg rounded-3xl backdrop-blur-xl p-10 transition-all duration-500 ${
            isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
          }`}
        >
          <div className="text-center mb-8">
            <div className={`p-6 rounded-full mb-6 inline-block ${isDarkMode ? "bg-blue-500/20" : "bg-blue-100"}`}>
              <i className={`fas fa-user text-4xl ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}></i>
            </div>
            <h2 className={`text-3xl font-bold mb-3 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Welcome!</h2>
            <p className={`text-base ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Enter your Codeforces username to get started</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                <i className={`fas fa-user mr-2 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}></i>
                Codeforces Username
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="e.g., tourist"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={`w-full px-6 py-4 border-2 rounded-2xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-lg ${
                    isDarkMode ? "bg-slate-800/50 border-slate-600 text-slate-100 placeholder-slate-400" : "bg-white border-slate-200 text-slate-900 placeholder-slate-500"
                  }`}
                  disabled={isLoading}
                />
                <div className={`absolute right-5 top-1/2 transform -translate-y-1/2 ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>
                  <i className="fas fa-user text-lg"></i>
                </div>
              </div>
            </div>

            {error && (
              <div className={`p-4 rounded-xl text-sm ${isDarkMode ? "bg-red-500/10 text-red-300 border border-red-500/20" : "bg-red-50 text-red-600 border border-red-200"}`}>
                <i className="fas fa-exclamation-circle mr-2"></i>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full px-6 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
              }`}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-3"></i>
                  Verifying...
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt mr-3"></i>
                  Get Started
                </>
              )}
            </button>
          </form>

          <div className={`mt-8 p-6 rounded-2xl ${isDarkMode ? "bg-slate-700/50" : "bg-slate-100"}`}>
            <h3 className={`text-base font-semibold mb-3 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
              <i className="fas fa-info-circle mr-2 text-blue-500"></i>
              What you'll get:
            </h3>
            <ul className={`text-sm space-y-2 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3 text-green-500"></i>
                Filter problems by solved/unsolved status
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3 text-green-500"></i>
                Track your progress across all problems
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3 text-green-500"></i>
                View detailed statistics about your performance
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle mr-3 text-green-500"></i>
                Get personalized problem recommendations
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserLogin;
